import React, { useRef, useState } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { ActionButton, ActionButtonV2 } from '@fabric-ui/components/Table';
import { Exploded, Function, Key, Minus, Plus } from '@fabric-ui/icons';
import { dotStyle, flex } from '@fabric-ui/theme';
import { ellipsis, iconSize, noop } from '@fabric-ui/utils';
import { Box, Button, useTheme } from '@mui/material';
import { useValidateMapping } from '../hooks';
import EditorPopper, { dropStyle } from './EditorPopper';
import ScriptEditorTest from './ScriptEditorTest';
import { FieldNameType, FieldValueType, MappingType, MappingValidationProps } from './types';
import { itemStyle } from './utils';

const key = <Key />;

function hoverIconStyle() {
  return {
    color: 'var(--color-content-interactive-secondary-default)',
    ...dotStyle(undefined, 16),
    border: `1.5px solid var(--color-content-interactive-secondary-default)`,
  };
}

interface MappingItemProps {
  label: React.ReactNode;
  isEditable?: boolean;
  popperProps?: {
    type: MappingType;
    modelFieldName: string;
    fieldType: string;
    entityName: string;
    mappingProps: MappingValidationProps;
    exploded?: boolean;
    compositeValue?: string[];
    replicaNumber?: number;
  };
  onMap?(...args: unknown[]): unknown;
  isMissing?: boolean;
  onClick?(...args: unknown[]): unknown;
  onDoubleClick?(...args: unknown[]): unknown;
  onDragStart?(...args: unknown[]): unknown;
  onDragEnd?(...args: unknown[]): unknown;
  draggable?: boolean;
  isUsed?: boolean;
  isSelected?: boolean;
  endAdornment?: React.ReactNode;
  size?: 'medium' | 'small';
  ref?: React.ForwardedRef<HTMLElement>;
  editorTypes?: MappingType[];
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  exploded?: boolean;
}

export const MappingItem = ({
  label,
  isEditable = false,
  popperProps,
  isMissing = false,
  onMap = noop,
  onClick,
  onDoubleClick,
  onDragStart,
  onDragEnd,
  draggable = true,
  isUsed,
  isSelected,
  endAdornment = isUsed === undefined ? null : isUsed ? <Minus className="hover-item" /> : <Plus className="hover-item" />,
  size = 'medium',
  ref,
  editorTypes,
  onMouseEnter,
  onMouseLeave,
}: MappingItemProps) => {
  const theme = useTheme();
  const timer = useRef<ReturnType<typeof setTimeout>>(null);

  const handleClick = (...args: unknown[]) => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    timer.current = setTimeout(() => {
      onClick?.(...args);
    }, 300);
  };

  const handleDoubleClick = (...args: unknown[]) => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    onDoubleClick?.(...args);
  };

  return (
    <Box
      ref={ref}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragCapture={onDragEnd}
      onDoubleClick={onDoubleClick && handleDoubleClick}
      onClick={onDoubleClick ? handleClick : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        ...itemStyle(size === 'small'),
        background: 'var(--color-surface-elevated-20)',
        ...(draggable && { cursor: 'move', ':active': { cursor: 'grabbing' } }),
        ...(isUsed ? { background: 'var(--color-surface-interactive-modifier-default)' } : {}),
        ...(isMissing && { background: 'var(--color-surface-status-danger-primary)' }),
        ...(popperProps?.type && { padding: '8px 0' }),
        ...(isSelected ? theme.components!.MuiTableRow!.styleOverrides!.root!['&.active-row'] : {}),
        position: 'relative',
        '.hover-item': {
          opacity: 0,
          ...hoverIconStyle(),
          transition: theme.transitions.create(['opacity']),
        },
        ':hover .hover-item': { opacity: 1 },
      }}>
      {popperProps ? (
        <ScriptOrLiteralMappingItem
          label={label}
          isEditable={isEditable}
          {...popperProps}
          onMap={onMap}
          size={size}
          editorTypes={editorTypes}
        />
      ) : (
        <Box sx={{ ...flex.itemsCenter, gap: '12px', width: '100%' }}>
          {label}
          {endAdornment}
        </Box>
      )}
    </Box>
  );
};

interface EntityFieldMappingItemProps {
  label: React.ReactNode;
  isKey?: boolean;
  isEditable?: boolean;
  onKeyChange?(...args: unknown[]): unknown;
  size?: 'medium' | 'small';
  hideKeyBtn: boolean;
  ref?: React.ForwardedRef<HTMLElement>;
}
export const EntityFieldMappingItem = ({
  label,
  isKey = false,
  isEditable = false,
  onKeyChange = noop,
  hideKeyBtn = false,
  size = 'medium',
  ref,
}: EntityFieldMappingItemProps) => (
  <Box ref={ref} sx={entityFieldStyle(isKey, isEditable, size === 'small')}>
    <header>
      {label}
      {!hideKeyBtn && (
        <ActionButtonV2 label={isEditable ? (isKey ? 'Remove Key' : 'Set as Key') : ''} onClick={() => onKeyChange(!isKey)} icon={key} />
      )}
    </header>
  </Box>
);

interface ScriptOrLiteralMappingItemProps {
  label: React.ReactNode;
  isEditable: boolean;
  type: MappingType;
  exploded?: boolean;
  modelFieldName: string;
  fieldType: string;
  onMap(...args: unknown[]): unknown;
  size?: 'medium' | 'small';
  mappingProps: MappingValidationProps;
  editorTypes?: MappingType[];
  compositeValue?: string[];
  entityName?: string;
  replicaNumber?: number;
}

const ScriptOrLiteralMappingItem: React.FC<ScriptOrLiteralMappingItemProps> = ({
  label,
  isEditable,
  type,
  exploded,
  modelFieldName,
  fieldType,
  onMap,
  size = 'medium',
  mappingProps,
  editorTypes,
  compositeValue,
  entityName,
  replicaNumber,
}) => {
  const [showEditor, setShowEditor] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [isEditorMaximized, setIsEditorMaximized] = useState(false);
  const [editorType, setEditorType] = useState(type);
  const [editorValue, setEditorValue] = useState(label);
  const dropRef = useRef(undefined);

  const toggleEditor = () => {
    setShowEditor(prev => !prev);
    setEditorType(type);
    setEditorValue(label);
    setShowTest(false);
    setIsEditorMaximized(false);
  };

  const { mutateAsync: validate, isPending: loadingValidate } = useValidateMapping();

  const buttons = [
    { onActionClick: toggleEditor, children: 'Cancel' },
    ...(editorType === MappingType.Composite
      ? []
      : [
          {
            onActionClick: (mappingValue: string, mappingType: MappingType) => {
              const { mapping } = mappingProps;
              if (mapping.fieldValueType === FieldValueType.MappingValue) {
                validate({
                  ...mappingProps,
                  mapping: {
                    ...mapping,
                    modelFieldName: mapping.fieldNameType === FieldNameType.Model ? mapping.modelName : mapping.metadataName,
                    mappingValue,
                    mappingType,
                  },
                });
              }
            },
            children: 'Validate',
            variant: 'outlined' as any,
            isDisabled: () => loadingValidate,
            loading: loadingValidate,
            sx: { mr: -1 },
          },
        ]),
    {
      onActionClick: (value, type, isExploded) => {
        setEditorValue(value);
        setEditorType(type);
        onMap(value, type, isExploded, toggleEditor);
      },
      variant: 'contained' as any,
      children: 'Apply',
      isDisabled: value => (type === MappingType.Composite ? value?.length === 0 : !value),
    },
  ];

  const functionButton = ActionButton(`${isEditable ? 'Edit' : 'Show'} Function`, toggleEditor, <Function style={iconSize(24)} />);
  const explodeButton = ActionButton(
    `Explode`,
    noop,
    <Exploded style={{ ...iconSize(20), color: 'var(--color-content-interactive-secondary-default)' }} />
  );

  return (
    <>
      <Box ref={dropRef} sx={mappingItemDropStyle(showEditor, 'right', size === 'small')} tabIndex={0} onClick={toggleEditor}>
        <AvTooltip title={label}>
          <header>
            {exploded && explodeButton}
            {type === MappingType.Script && functionButton}
            {type === MappingType.Literal ? `"${label}"` : label}
          </header>
        </AvTooltip>
      </Box>
      {showEditor && (
        <EditorPopper
          handleClose={toggleEditor}
          type={editorType}
          exploded={exploded}
          setType={v => setEditorType(v)}
          anchorEl={dropRef.current}
          open={showEditor}
          buttons={buttons}
          modelFieldName={modelFieldName}
          value={editorValue as string}
          onChange={v => setEditorValue(v)}
          editable={isEditable}
          fieldType={fieldType}
          mappingTypes={editorTypes}
          compositeValue={compositeValue}
          maximize={(editorType === MappingType.Script && isEditorMaximized) || undefined}
          entityName={entityName}
          replicaNumber={replicaNumber}
          codeEditorControls={
            isEditable ? (
              <Button
                onClick={() => {
                  setShowTest(true);
                  setIsEditorMaximized(true);
                }}
                sx={{ ml: 1, mt: 1, mb: 1 }}
                variant="outlined"
                size="xSmall">
                Test Script
              </Button>
            ) : undefined
          }
          ScriptEditor={!showTest ? undefined : <ScriptEditorTest />}
        />
      )}
    </>
  );
};

const mappingItemDropStyle = (selected, borderSide, isSmall) => ({
  ...itemStyle(isSmall),
  header: {
    ...ellipsis,
    '.MuiButton-root': {
      minWidth: 'unset',
      mr: 1,
      svg: {
        height: 17,
        width: 17,
        color: 'var(--color-content-base-tertiary)',
      },
    },
  },
  pr: 1,
  pl: '14px',
  cursor: 'pointer',
  border: '2px solid',
  borderColor: 'transparent',
  ...(selected && {
    borderColor: 'var(--color-border-interactive-secondary-active)',
  }),
  ...dropStyle(selected, borderSide),
});

const entityFieldStyle = (isKey, isEditable, isSmall) => ({
  ...itemStyle(isSmall),
  pr: 1,
  header: {
    ...flex.justifyBetweenCenter,
    width: '100%',
    '.MuiButton-root': {
      opacity: !isEditable && !isKey ? 0 : undefined,
      pointerEvents: isEditable ? undefined : 'none',
      svg: {
        width: 18,
        height: 18,
        color: 'inherit',
      },
    },
  },
  ...(!isKey && isEditable && { '&:not(:hover) header .MuiButton-root': { opacity: 0 } }),
});
